import { Marker } from "@vis.gl/react-maplibre";
import { useEffect, useState } from "react";
import type { CompassDir } from "../../lib/types";
import { knotsColor, speedArcRatio } from "../../lib/utils";
import styles from "./MapMarkers.module.css";

// Shared arc stroke width — both the unselected disc's arc and the
// selected teardrop's arc render at this thickness so the speed
// indicator feels visually consistent across states.
const ARC_STROKE_WIDTH = 4;

// Unselected marker: 28×28 outer box, 18×18 inner direction disc, arc
// centred at (14, 14). Arc r=11.5 + sw=4 places the arc's inner edge at
// r=9.5 and outer edge at r=13.5 — the 28×28 box has a 0.5px margin to
// the arc's outer extent.
const MARKER_SIZE = 28;
const MARKER_CENTER = MARKER_SIZE / 2;
const ARC_RADIUS = 11.5;
const ARC_CIRCUMFERENCE = 2 * Math.PI * ARC_RADIUS;

// Selected teardrop: 48×68 viewBox, head circle r=20 at (24, 24), tail
// tip at (24, 67). The inner content (arc + disc) is intentionally
// larger than the unselected — the arc reads at ~32×32, giving the
// picked spot more visual weight. Head r=20 leaves ~4px of teardrop
// breathing room around the arc's outer edge (r=16).
const SELECTED_W = 48;
const SELECTED_H = 68;
const SELECTED_CENTER_X = 24;
const SELECTED_CENTER_Y = 24;
const SELECTED_TAIL_Y = 67;
const SELECTED_HEAD_R = 20;
const SELECTED_ARC_RADIUS = 14;
const SELECTED_ARC_CIRCUMFERENCE = 2 * Math.PI * SELECTED_ARC_RADIUS;
// 11.5 (not 11) so the 1px stroke's outer edge lands at r=12 — flush
// with the arc's inner edge (SELECTED_ARC_RADIUS − ARC_STROKE_WIDTH/2),
// closing the half-pixel gap that otherwise let the teardrop fill show
// through between the disc and the arc.
const SELECTED_INNER_R = 11.5;
// 1px hairline tracing the arc band's outer boundary — strictly a
// selected-state detail; the unselected disc doesn't need it because
// the dark basemap already frames the arc.
const SELECTED_ARC_OUTLINE_R = SELECTED_ARC_RADIUS + ARC_STROKE_WIDTH / 2;
const SELECTED_TEARDROP_PATH = `M${SELECTED_CENTER_X} ${SELECTED_TAIL_Y} C ${SELECTED_CENTER_X} 54, 44 38, 44 ${SELECTED_CENTER_Y} A ${SELECTED_HEAD_R} ${SELECTED_HEAD_R} 0 1 0 4 ${SELECTED_CENTER_Y} C 4 38, ${SELECTED_CENTER_X} 54, ${SELECTED_CENTER_X} ${SELECTED_TAIL_Y} Z`;
// Pixel offset that shifts the selected marker UP so its tail tip
// lands on the spot's lat/lon. We avoid maplibre's `anchor` prop here
// because @vis.gl/react-maplibre's <Marker> only reads `anchor` on
// init — it doesn't reapply when the prop changes. A click that swaps
// the marker from unselected → selected would otherwise leave the
// anchor at "center" forever. Offset, by contrast, is reapplied on
// every render (the wrapper calls `marker.setOffset(...)`).
const SELECTED_TIP_OFFSET_Y = -(SELECTED_TAIL_Y - SELECTED_H / 2);

/**
 * Drives the arrow's `transform: rotate(...)` value with continuous,
 * shortest-path interpolation so the CSS transition crosses 0/360 the
 * short way (e.g. 350° → 10° animates 20° clockwise, not 340° back the
 * other way). The returned number can exceed [0, 360); CSS doesn't
 * mind, and keeping the running sum is what makes the shortest-path
 * pick correct across many updates.
 */
function useContinuousRotation(dirDeg: number | null): number {
  const initial = dirDeg !== null ? dirDeg - 90 : 0;
  const [rot, setRot] = useState<number>(initial);
  useEffect(() => {
    if (dirDeg === null) return;
    const target = dirDeg - 90;
    setRot((prev) => {
      // Normalize `target - prev` to (-180, 180] so we always pick the
      // shorter arc, then accumulate onto `prev`.
      const delta = (((target - prev) % 360) + 540) % 360 - 180;
      return prev + delta;
    });
  }, [dirDeg]);
  return rot;
}

const DIR_DEG: Record<CompassDir, number> = {
  N: 0, NNE: 22.5, NE: 45, ENE: 67.5,
  E: 90, ESE: 112.5, SE: 135, SSE: 157.5,
  S: 180, SSW: 202.5, SW: 225, WSW: 247.5,
  W: 270, WNW: 292.5, NW: 315, NNW: 337.5,
};

function angleDiff(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

// ─── Region marker ───────────────────────────────────────────────────────────

interface RegionMarkerProps {
  longitude: number;
  latitude: number;
  count: number;
  label: string;
  onClick?: () => void;
}

/**
 * 32×32 dark disc with a spot count, rendered at the centroid of a region.
 * Used on the regions map and on the spots map when the viewport is too
 * zoomed-out to show individual spot markers.
 */
export function RegionMarker({
  longitude,
  latitude,
  count,
  label,
  onClick,
}: RegionMarkerProps) {
  return (
    <Marker longitude={longitude} latitude={latitude} anchor="center">
      <button
        type="button"
        className={styles.regionMarker}
        onClick={onClick}
        aria-label={`${label} — ${count} spots`}
      >
        <span className={styles.regionCount}>{count}</span>
      </button>
    </Marker>
  );
}

// ─── Custom location marker ─────────────────────────────────────────────────

interface CustomLocationMarkerProps {
  longitude: number;
  latitude: number;
}

/**
 * Dropped-pin marker for an arbitrary lat/lon the user long-pressed on
 * the map. Reuses the same teardrop silhouette as the selected
 * SpotMarker so the pin reads as "I just dropped a marker here", but
 * with no inner direction arrow — there's no spot curation, so the
 * tint is neutral.
 */
export function CustomLocationMarker({
  longitude,
  latitude,
}: CustomLocationMarkerProps) {
  return (
    <Marker longitude={longitude} latitude={latitude} anchor="bottom">
      <span className={styles.customLocationPin} aria-label="Custom location">
        <svg width="32" height="48" viewBox="0 0 32 48" fill="none" aria-hidden>
          <path
            d="M16 47 C 16 36, 28 28, 28 16 A 12 12 0 1 0 4 16 C 4 28, 16 36, 16 47 Z"
            fill="var(--map-marker-surface-default)"
            stroke="var(--map-spot-marker-border-selected)"
            strokeWidth="1"
          />
          <circle
            cx="16"
            cy="16"
            r="4"
            fill="var(--map-region-marker-foreground)"
          />
        </svg>
      </span>
    </Marker>
  );
}

// ─── Spot marker ─────────────────────────────────────────────────────────────

interface SpotMarkerProps {
  longitude: number;
  latitude: number;
  /** Meteorological wind direction (degrees, where wind is FROM). */
  dirDeg: number | null;
  /** Representative wind speed for the marker's selected day, in m/s.
   *  Drives the outer-ring arc (length + colour). `null` = skip day /
   *  no data → arc is hidden. */
  speedMs: number | null;
  /** Spot's ideal directions — the only curated direction set. */
  idealDirs: readonly CompassDir[];
  /** Renders the selected teardrop variant when true. */
  selected?: boolean;
  /** True when the user is hovering the corresponding spot in the side
   *  list — scales the marker up so the map<->list link reads clearly.
   *  Desktop-only behaviour (touch devices don't fire hover). */
  hovered?: boolean;
  onClick?: () => void;
}

/**
 * Map-marker fill colour — pure direction match, no longer mixed with
 * speed. Four tiers, derived from the angular distance from the
 * forecast direction to the nearest ideal direction:
 *
 *   • ≤ 22.5°  → green  (ideal)
 *   • ≤ 45°    → blue   (adjacent — one compass cell out; absorbs
 *                       forecast tolerance)
 *   • ≥ 157.5° → orange (offshore — within ±22.5° of the opposite of
 *                       any ideal direction)
 *   • otherwise → muted (mid-quadrant, neither working nor offshore)
 */
function spotMarkerColor(
  dirDeg: number | null,
  idealDirs: readonly CompassDir[],
): string {
  if (dirDeg === null || idealDirs.length === 0) {
    return "var(--wind-direction-neutral-default)";
  }
  let minDelta = 180;
  for (const d of idealDirs) {
    const delta = angleDiff(dirDeg, DIR_DEG[d]);
    if (delta < minDelta) minDelta = delta;
  }
  if (minDelta <= 22.5) return "var(--wind-direction-prime-default)";
  if (minDelta <= 45) return "var(--wind-direction-good-default)";
  if (minDelta >= 157.5) return "var(--wind-direction-unlikely-default)";
  return "var(--wind-direction-neutral-default)";
}

/**
 * Spot marker. Default state is a 28×28 outer box with an 18×18 colored
 * inner disc + a wind-speed arc orbiting it. The disc's small arrow
 * points in the direction the wind is *blowing toward* (i.e. away from
 * its source), matching the spot-detail compass needle and the rest of
 * the app's arrows. Rotation is `dirDeg − 90` because the SVG glyph
 * natively points along the −x axis (left); a south wind (`dirDeg =
 * 180`) renders pointing up. Selected state is a 48×68 teardrop pin
 * with the same arc + disc inside (sized up to ~32×32), offset so the
 * tail tip lands on the spot's lat/lon.
 */
export function SpotMarker({
  longitude,
  latitude,
  dirDeg,
  speedMs,
  idealDirs,
  selected = false,
  hovered = false,
  onClick,
}: SpotMarkerProps) {
  const arrowRot = useContinuousRotation(dirDeg);

  // Outer-ring arc length + colour. When `speedMs === null` (skip day),
  // the arc hides itself by parking dashoffset at full circumference;
  // the circle stays in the DOM so the CSS transition can ease back in
  // when speed data appears for another day. Sub-3 kn hours render in
  // `--wind-nil` (12% alpha white) — same treatment HourlyChart applies
  // to dead-calm bars, so an unrideable spot reads as muted across the
  // whole app instead of getting a misleading bright knotsColor stop.
  const speedKn = speedMs !== null ? speedMs * 1.944 : 0;
  const arcRatio = speedMs !== null ? speedArcRatio(speedKn) : 0;
  const arcOffset = ARC_CIRCUMFERENCE * (1 - arcRatio);
  const arcStroke =
    speedMs === null
      ? "transparent"
      : speedKn < 3
        ? "var(--wind-nil)"
        : knotsColor(speedKn);
  const fill = spotMarkerColor(dirDeg, idealDirs);

  if (selected) {
    return (
      <Marker
        longitude={longitude}
        latitude={latitude}
        offset={[0, SELECTED_TIP_OFFSET_Y]}
      >
        <button
          type="button"
          className={[
            styles.spotMarkerSelected,
            hovered && styles.spotMarkerListHovered,
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={onClick}
          aria-label="Selected spot"
        >
          {/* Teardrop pin — the selected variant intentionally has a
              distinct silhouette so the user can spot the selection at
              a glance among the round unselected markers. Geometry
              defined by the SELECTED_* constants at the top of the
              file (48×68 viewBox, head r=20, tail tip at y=67). Inner
              content is sized larger than the unselected disc so the
              picked spot has more visual weight. */}
          <svg
            width={SELECTED_W}
            height={SELECTED_H}
            viewBox={`0 0 ${SELECTED_W} ${SELECTED_H}`}
            fill="none"
            aria-hidden
          >
            <path
              d={SELECTED_TEARDROP_PATH}
              fill="var(--surface-inverse)"
              stroke="#fff"
              strokeWidth="1"
            />
            {/* Background track behind the speed arc — solid black so
                the colored active arc reads with maximum contrast on
                top, regardless of which knotsColor stop it lands on. */}
            <circle
              className={styles.spotMarkerSelectedTrack}
              cx={SELECTED_CENTER_X}
              cy={SELECTED_CENTER_Y}
              r={SELECTED_ARC_RADIUS}
              stroke="var(--foreground-inverse)"
              strokeWidth={ARC_STROKE_WIDTH}
              fill="none"
            />
            {/* Wind-speed arc — sized larger than the unselected disc's
                arc so the selected marker's inner content reads at
                roughly 32×32 overall. */}
            <circle
              className={styles.spotMarkerArcCircle}
              cx={SELECTED_CENTER_X}
              cy={SELECTED_CENTER_Y}
              r={SELECTED_ARC_RADIUS}
              stroke={arcStroke}
              strokeWidth={ARC_STROKE_WIDTH}
              strokeLinecap="butt"
              strokeDasharray={`${SELECTED_ARC_CIRCUMFERENCE} ${SELECTED_ARC_CIRCUMFERENCE}`}
              strokeDashoffset={SELECTED_ARC_CIRCUMFERENCE * (1 - arcRatio)}
              transform={`rotate(-90 ${SELECTED_CENTER_X} ${SELECTED_CENTER_Y})`}
            />
            {/* 1px black hairline tracing the outer edge of the arc
                band. Sits on top of both the track and the active arc
                so the boundary reads cleanly against the teardrop's
                cream fill. */}
            <circle
              cx={SELECTED_CENTER_X}
              cy={SELECTED_CENTER_Y}
              r={SELECTED_ARC_OUTLINE_R}
              stroke="var(--foreground-inverse)"
              strokeWidth="1"
              fill="none"
            />
            {/* Inner 22×22 colored direction disc, drawn on top of the
                arc so the disc edge covers any half-pixel overdraw.
                Black border for legibility against the light teardrop
                fill. */}
            <circle
              className={styles.spotMarkerSelectedInner}
              cx={SELECTED_CENTER_X}
              cy={SELECTED_CENTER_Y}
              r={SELECTED_INNER_R}
              fill={fill}
              stroke="var(--foreground-inverse)"
              strokeWidth="1"
            />
          </svg>
          {dirDeg !== null && (
            <svg
              className={styles.spotMarkerSelectedArrow}
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              style={{ transform: `rotate(${arrowRot}deg)` }}
              aria-hidden
            >
              <line
                x1="8"
                y1="5"
                x2="2"
                y2="5"
                stroke="var(--map-spot-marker-foreground)"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
              <polyline
                points="4.5,2.5 2,5 4.5,7.5"
                fill="none"
                stroke="var(--map-spot-marker-foreground)"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
      </Marker>
    );
  }

  return (
    <Marker
      longitude={longitude}
      latitude={latitude}
      offset={[0, 0]}
    >
      <button
        type="button"
        className={[
          styles.spotMarker,
          hovered && styles.spotMarkerListHovered,
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={onClick}
        aria-label={`Spot at ${latitude}, ${longitude}`}
      >
        {/* Outer wind-speed arc. Rotated −90° so the arc starts at the
            12 o'clock position and grows clockwise as speed increases.
            stroke-dashoffset + stroke transition (set in CSS) animate
            the change when the user picks a different day. */}
        <svg
          className={styles.spotMarkerArc}
          width={MARKER_SIZE}
          height={MARKER_SIZE}
          viewBox={`0 0 ${MARKER_SIZE} ${MARKER_SIZE}`}
          fill="none"
          aria-hidden
        >
          {/* Background track — always a full circle so the active arc
              reads as "fill level out of N", not "some random arc". */}
          <circle
            cx={MARKER_CENTER}
            cy={MARKER_CENTER}
            r={ARC_RADIUS}
            stroke="var(--map-marker-arc-track)"
            strokeWidth={ARC_STROKE_WIDTH}
            fill="none"
          />
          <circle
            className={styles.spotMarkerArcCircle}
            cx={MARKER_CENTER}
            cy={MARKER_CENTER}
            r={ARC_RADIUS}
            stroke={arcStroke}
            strokeWidth={ARC_STROKE_WIDTH}
            strokeLinecap="butt"
            strokeDasharray={`${ARC_CIRCUMFERENCE} ${ARC_CIRCUMFERENCE}`}
            strokeDashoffset={arcOffset}
            transform={`rotate(-90 ${MARKER_CENTER} ${MARKER_CENTER})`}
          />
        </svg>
        {/* Inner 18×18 colored direction disc, sitting inside the 28×28
            outer arc box. `border-radius: 50%` plus `corner-shape: round`
            in the CSS opts out of the global squircle treatment so this
            renders as a real circle. */}
        <span className={styles.spotMarkerInner} style={{ background: fill }}>
          {dirDeg !== null && (
            <svg
              className={styles.spotMarkerArrow}
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              style={{ transform: `rotate(${arrowRot}deg)` }}
              aria-hidden
            >
              <line
                x1="8"
                y1="5"
                x2="2"
                y2="5"
                stroke="var(--map-spot-marker-foreground)"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
              <polyline
                points="4.5,2.5 2,5 4.5,7.5"
                fill="none"
                stroke="var(--map-spot-marker-foreground)"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
      </button>
    </Marker>
  );
}
